import axios from 'axios';
import { ApiConfig } from '../config/ApiConfig';

const API_BASE = `${ApiConfig.apiURL}/api/auth/2fa`;

const withAuth = (token) => ({
    headers: {
        Authorization: `Bearer ${token}`,
    },
});

const getData = (response) => response?.data || {};

const twoFactorAuth = {
    getStatus: async (token) => getData(await axios.get(`${API_BASE}/status`, withAuth(token))),

    // Enrolment endpoints (authenticated session)
    totpSetup: async (token) => getData(await axios.post(`${API_BASE}/totp/setup`, {}, withAuth(token))),
    totpVerify: async (token, code) => getData(await axios.post(`${API_BASE}/totp/verify`, { code }, withAuth(token))),
    emailSend: async (token) => getData(await axios.post(`${API_BASE}/email/send`, {}, withAuth(token))),
    emailVerify: async (token, code) => getData(await axios.post(`${API_BASE}/email/verify`, { code }, withAuth(token))),
    smsSetup: async (token, phone) => getData(await axios.post(`${API_BASE}/sms/setup`, { phone }, withAuth(token))),
    smsVerify: async (token, code) => getData(await axios.post(`${API_BASE}/sms/verify`, { code }, withAuth(token))),

    // Biometric 2FA enrolment — mint a device-trust token for THIS device that the
    // app stores in the keychain behind a Face ID / Touch ID gate. Requires a fresh
    // step-up token (proving a recent second-factor re-auth). `deviceId` must match
    // the id the login flow binds the trust to (getOrCreateDeviceTrustId).
    deviceTrustIssue: async (token, { stepUpToken, deviceId } = {}) => getData(await axios.post(
        `${API_BASE}/device-trust/issue`,
        { deviceId },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                ...(stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : {}),
                ...(deviceId ? { 'x-device-id': deviceId } : {}),
            },
        },
    )),

    // Management endpoints (authenticated session) — disable a method / rotate codes
    totpDisable: async (token) => getData(await axios.post(`${API_BASE}/totp/disable`, {}, withAuth(token))),
    emailDisable: async (token) => getData(await axios.post(`${API_BASE}/email/disable`, {}, withAuth(token))),
    smsDisable: async (token) => getData(await axios.post(`${API_BASE}/sms/disable`, {}, withAuth(token))),
    regenerateRecoveryCodes: async (token) => getData(await axios.post(`${API_BASE}/recovery/regenerate`, {}, withAuth(token))),

    disableMethod: async (token, methodKey) => {
        const map = {
            totp: `${API_BASE}/totp/disable`,
            email: `${API_BASE}/email/disable`,
            sms: `${API_BASE}/sms/disable`,
        };
        const url = map[methodKey];

        if (!url) {
            throw new Error(`Unsupported 2FA method: ${methodKey}`);
        }

        return getData(await axios.post(url, {}, withAuth(token)));
    },

    // Login challenge endpoints (pre-auth token, no bearer token yet)
    challengeEmailSend: async (preAuthToken) => getData(await axios.post(`${API_BASE}/challenge/email/send`, { preAuthToken })),
    challengeSmsSend: async (preAuthToken) => getData(await axios.post(`${API_BASE}/challenge/sms/send`, { preAuthToken })),
    challengeVerify: async (preAuthToken, method, payload = {}) => getData(await axios.post(`${API_BASE}/challenge/verify`, {
        preAuthToken,
        method,
        ...payload,
    })),
};

export default twoFactorAuth;

